<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import KanbanColumn from './KanbanColumn.vue';
import { useKanbanDragDrop } from 'dashboard/composables/kanban/useKanbanDragDrop';
import { getEdgeScroll } from './autoScroll';

const props = defineProps({
  funnel: {
    type: Object,
    required: true,
  },
  groupedConversations: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'cardClick',
  'cardMove',
  'cardRemove',
  'transferToFunnel',
  'winDeal',
  'loseDeal',
  'viewDetails',
  'updateValue',
  'updateAppointment',
  'sendQuickMessage',
  'addLead',
  'updateTemperature',
]);

const boardRef = ref(null);

// Composable para drag and drop
const {
  dragOverColumn,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
} = useKanbanDragDrop();

// Computed para obter colunas ordenadas
const orderedColumns = computed(() => {
  if (!props.funnel?.stages) return [];

  return Object.entries(props.funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([stageName, stageConfig]) => ({
      name: stageName,
      ...stageConfig,
      conversations: props.groupedConversations[stageName] || [],
    }));
});

// Methods
const handleCardClick = conversationId => {
  emit('cardClick', conversationId);
};

const handleCardRemove = conversation => {
  emit('cardRemove', conversation);
};

const handleTransferToFunnel = conversation => {
  emit('transferToFunnel', conversation);
};

const handleWinDeal = data => {
  emit('winDeal', data);
};

const handleLoseDeal = data => {
  emit('loseDeal', data);
};

const handleViewDetails = data => {
  emit('viewDetails', data);
};

const handleUpdateValue = data => {
  emit('updateValue', data);
};

const handleUpdateAppointment = data => {
  emit('updateAppointment', data);
};

const handleSendQuickMessage = data => {
  emit('sendQuickMessage', data);
};

const handleAddLead = stageName => {
  emit('addLead', stageName);
};

const handleUpdateTemperature = data => {
  emit('updateTemperature', data);
};

const handleCardMove = async moveData => {
  const { conversationId, fromColumn, toColumn } = moveData;

  if (fromColumn !== toColumn) {
    emit('cardMove', {
      conversationId,
      fromStage: fromColumn,
      toStage: toColumn,
    });
  }
};

// Setup drag and drop handlers
const onDragStart = (conversationId, fromColumn) => {
  handleDragStart({
    conversationId,
    fromColumn,
  });
};

const onDragOver = (event, columnName) => {
  handleDragOver(event, columnName);
};

const onDragLeave = (event, columnName) => {
  handleDragLeave(event, columnName);
};

const onDrop = (event, columnName) => {
  const result = handleDrop(event, columnName);
  if (result) {
    handleCardMove(result);
  }
};

// Auto-scroll horizontal nas bordas durante o drag (o drag HTML5 não rola o
// container sozinho — sem isso é impossível arrastar da 1ª à última coluna).
let scrollDirection = 0;
let scrollSpeed = 0;
let rafId = null;

const stopAutoScroll = () => {
  scrollDirection = 0;
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

const autoScrollStep = () => {
  if (!scrollDirection || !boardRef.value) {
    rafId = null;
    return;
  }
  boardRef.value.scrollLeft += scrollDirection * scrollSpeed;
  rafId = requestAnimationFrame(autoScrollStep);
};

const onBoardDragOver = event => {
  const board = boardRef.value;
  if (!board) return;

  const { direction, speed } = getEdgeScroll(
    event.clientX,
    board.getBoundingClientRect()
  );
  scrollDirection = direction;
  scrollSpeed = speed;

  if (scrollDirection && !rafId) {
    rafId = requestAnimationFrame(autoScrollStep);
  }
};

onMounted(() => {
  // dragend não borbulha até o board quando o card some do DOM no drop;
  // ouvir na window garante parar o auto-scroll em qualquer desfecho.
  window.addEventListener('dragend', stopAutoScroll);
  window.addEventListener('drop', stopAutoScroll);
});

onBeforeUnmount(() => {
  stopAutoScroll();
  window.removeEventListener('dragend', stopAutoScroll);
  window.removeEventListener('drop', stopAutoScroll);
});
</script>

<template>
  <div
    ref="boardRef"
    class="kanban-board p-4 bg-n-background overflow-x-auto overflow-y-hidden h-full"
    @dragover="onBoardDragOver"
  >
    <div class="flex gap-4 h-full w-max">
      <KanbanColumn
        v-for="column in orderedColumns"
        :key="column.name"
        :stage-name="column.name"
        :stage-config="column"
        :conversations="column.conversations"
        :funnel-id="funnel.id"
        :is-drag-over="dragOverColumn === column.name"
        @card-click="handleCardClick"
        @card-remove="handleCardRemove"
        @transfer-to-funnel="handleTransferToFunnel"
        @drag-start="onDragStart"
        @drag-over="onDragOver"
        @drag-leave="onDragLeave"
        @drop="onDrop"
        @win-deal="handleWinDeal"
        @lose-deal="handleLoseDeal"
        @view-details="handleViewDetails"
        @update-value="handleUpdateValue"
        @update-appointment="handleUpdateAppointment"
        @send-quick-message="handleSendQuickMessage"
        @add-lead="handleAddLead"
        @update-temperature="handleUpdateTemperature"
      />
    </div>
  </div>
</template>

<style scoped>
/* Scrollbar styling - respects theme via CSS variables */
.kanban-board {
  --scrollbar-track: #f1f5f9;
  --scrollbar-thumb: #cbd5e1;
  --scrollbar-thumb-hover: #94a3b8;
}

:root.dark .kanban-board,
.dark .kanban-board {
  --scrollbar-track: #1e293b;
  --scrollbar-thumb: #475569;
  --scrollbar-thumb-hover: #64748b;
}

.kanban-board::-webkit-scrollbar {
  height: 8px;
}

.kanban-board::-webkit-scrollbar-track {
  background: var(--scrollbar-track);
  border-radius: 4px;
}

.kanban-board::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 4px;
}

.kanban-board::-webkit-scrollbar-thumb:hover {
  background: var(--scrollbar-thumb-hover);
}
</style>
